import React from "react";

import classnames from "classnames";
import { stepCompleted } from "EmployeeApplication/actions";
import { useGetApplicationQuery, useUpdateEmployeePolicyMutation, getEmployeeForm, getEmployeeApplication, getDependents } from "EmployeeApplication/api";
import {
  employeePlanField,
  employeeEnrollmentField,
  dependentEnrollmentField,
  getEmployeeEnrollment,
  getDependentEnrollment,
  getEnrollmentSegment,
} from "EmployeeApplication/selectors";
import { findIndex, mapValues, keyBy } from "lodash";
import { useSelector } from "react-redux";
import { reduxForm, formValueSelector, Field } from "redux-form";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import formatCurrency from "Utils/formatCurrency";
import { validateRequired } from "Utils/Validations";
import BasicLoading from "ZeroRuby/BasicLoading";

import { chooseCoverageRows, getPersonFromId, totalCost } from "../Utils/chooseCoverage";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "Cobalt/Modal";
import { SaveButton } from "Common/forms/buttons";
import Header from "Cobalt/Header";
import { Table } from "Common/Table";

import PlanHeader from "./PlanHeader";

import type { FamilyInfo } from "../Utils/chooseCoverage";
import type { StepName } from "EmployeeApplication/actions";
import type { Plan, EmployeeForm, EmployeePolicy, EmployeeDependent, FamilyQuote, ContributionFormula } from "Utils/types";

import makeBem from "Utils/makeBem";

type Props = {
  plan: Plan;
  prices: Array<FamilyQuote>;
  familyInfo: Array<FamilyInfo>;
  contributions: ContributionFormula;
  openModal?: boolean;
  closeModal: () => void;
};

type ModalState = {
  show: boolean;
};

const bem = makeBem("ChooseCoverageModal");

class ChooseCoverageModal extends React.Component<Props, ModalState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      show: !!props.openModal,
    };
  }

  close = () => {
    this.props.closeModal();
    this.setState({ show: false });
  };

  open = (e: React.SyntheticEvent) => {
    e.preventDefault();
    this.setState({ show: true });
  };

  render() {
    return (
      <EnrollmentModal onHide={this.close} show={this.props.openModal || this.state.show} pricingType={this.props.prices[0].pricing_type} {...this.props} />
    );
  }
}

// @ts-expect-error
export const EnrollmentModal = ({ familyInfo, onHide, plan, prices, pricingType, contributions, show }) => {
  const { isLoading } = useGetApplicationQuery();
  const [updateEmployeeApplication] = useUpdateEmployeePolicyMutation();
  const employeeForm = useSelector(getEmployeeForm);
  const employeePolicy = useSelector(getEmployeeApplication);
  const dependents = useSelector(getDependents);

  const initialValues = {
    ...formFormatEnrollment(plan.type, pricingType, employeeForm, employeePolicy, dependents),
  };

  return (
    <Modal show={show} onHide={onHide} className={bem()}>
      {isLoading ? (
        <BasicLoading />
      ) : (
        <EnrollmentModalForm
          initialValues={initialValues}
          form={`${plan.type}Coverage`}
          updateEmployeeApplication={updateEmployeeApplication}
          plan={plan}
          pricingType={pricingType}
          familyInfo={familyInfo}
          prices={prices}
          employeeDependents={dependents}
          employeePolicyId={employeePolicy.id}
          contributions={contributions}
        />
      )}
    </Modal>
  );
};

const formSelector = (type: string) => formValueSelector(`${type}Coverage`);

const stepName = (type: string): StepName => (type === "medical" ? "chooseMedicalPlan" : type === "dental" ? "chooseDentalPlan" : "chooseVisionPlan");

const formFormatEnrollment = (
  type: "medical" | "dental" | "vision",
  pricingType: "age" | "segment" | "composite_segment",
  employeeForm: EmployeeForm | null | undefined,
  employeePolicy: EmployeePolicy | null | undefined,
  employeeDependents: Array<EmployeeDependent>,
) => {
  if (pricingType === "age") {
    const employeeEnrollment = getEmployeeEnrollment(employeePolicy, type);
    const dependentEnrollment = getDependentEnrollment(employeePolicy, type);
    return {
      employee: {
        isEnrolling: employeeEnrollment,
      },

      dependents: employeeForm
        ? employeeEnrollment === "Yes"
          ? mapValues(dependentEnrollment, (isEnrolling) => ({ isEnrolling }))
          : mapValues(dependentEnrollment, () => "No")
        : {},
    };
  }
  return {
    segment: getEnrollmentSegment(employeePolicy, employeeDependents, type),
  };
};

const formParseEnrollment = (
  type: "medical" | "dental" | "vision",
  pricingType: "age" | "segment" | "composite_segment",
  employeeDependents: Array<EmployeeDependent>,
  values: any,
): any => {
  if (pricingType === "segment" || pricingType === "composite_segment") {
    if (values.segment === "ee_only") {
      return {
        [employeeEnrollmentField(type)]: "Yes",
        [dependentEnrollmentField(type)]: mapValues(keyBy(employeeDependents, "id"), (_) => false),
      };
    }
    if (values.segment === "ee_spouse") {
      return {
        [employeeEnrollmentField(type)]: "Yes",
        [dependentEnrollmentField(type)]: mapValues(
          keyBy(employeeDependents, "id"),
          (d) => d.dependentType === "spouse" || d.dependentType === "domestic_partner",
        ),
      };
    }
    if (values.segment === "ee_children") {
      return {
        [employeeEnrollmentField(type)]: "Yes",
        [dependentEnrollmentField(type)]: mapValues(keyBy(employeeDependents, "id"), (d) => d.dependentType === "child"),
      };
    }
    if (values.segment === "ee_family") {
      return {
        [employeeEnrollmentField(type)]: "Yes",
        [dependentEnrollmentField(type)]: mapValues(keyBy(employeeDependents, "id"), (_) => true),
      };
    }
    if (values.segment === "none") {
      return {
        [employeeEnrollmentField(type)]: "No",
        [dependentEnrollmentField(type)]: mapValues(keyBy(employeeDependents, "id"), (_) => false),
      };
    }
    throw new Error(`Unrecognized segment ${JSON.stringify(values.segment)}`);
  } else {
    if (values.employee.isEnrolling === "No") {
      AnalyticsTracking.trackEvent("cta_clicked", {
        location: "employee_application_choose_coverage_modal",
        label: "decline",
      });

      return {
        [employeeEnrollmentField(type)]: "No",
        [dependentEnrollmentField(type)]: {},
      };
    }
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_modal",
      label: "enroll",
    });

    return {
      [employeeEnrollmentField(type)]: "Yes",
      [dependentEnrollmentField(type)]: mapValues(values.dependents, (d) => d.isEnrolling === "Yes"),
    };
  }
};

const withReduxForm = reduxForm({
  enableReinitialize: true,
  onSubmit: (values, _dispatch, props) =>
    props
      .updateEmployeeApplication({
        id: props.employeePolicyId,
        [employeePlanField(props.plan.type)]: props.plan.id,
        ...formParseEnrollment(props.plan.type, props.pricingType, props.employeeDependents, values),
      })
      .unwrap(),
  onSubmitSuccess: (_, dispatch, props) => dispatch(stepCompleted(stepName(props.plan.type))),
});

const EnrollmentModalForm = withReduxForm(({ handleSubmit, valid, plan, pricingType, familyInfo, prices, contributions }) => {
  const planType = plan.type.charAt(0).toUpperCase() + plan.type.slice(1);

  return (
    <>
      <ModalHeader closeButton>
        <Header level={7} mb={0}>
          {`Enroll In ${planType} Coverage`}
        </Header>
      </ModalHeader>
      <ModalBody>
        <div>
          <PlanHeader plan={plan} />
          <EnrollmentWarning type={plan.type} pricingType={pricingType} familyInfo={familyInfo} />

          <ChooseCoverageEnrollmentTable plan={plan} familyInfo={familyInfo} prices={prices} pricingType={pricingType} contributions={contributions} />
        </div>
      </ModalBody>
      <ModalFooter>
        <SaveButton onClick={handleSubmit} disabled={!valid} />
      </ModalFooter>
    </>
  );
});

const EnrollmentWarning = ({ type, pricingType, familyInfo }) => {
  const selector = formSelector(type);
  const show = useSelector((state) => selector(state, "employee.isEnrolling") === "No");

  if (pricingType === "age" && familyInfo.length > 1 && show) {
    return (
      <div className={bem("Alert")}>
        <b>Note:</b> If you decline coverage, you cannot enroll your family members.
      </div>
    );
  }
  return <div className={bem("Alert")}>&nbsp;</div>;
};

const ChooseCoverageEnrollmentTable = ({ familyInfo, plan, prices, pricingType, contributions }) => {
  const index = findIndex(prices, (p) => p.planId === plan.id);
  const selector = formSelector(plan.type);
  const employee = useSelector(getEmployeeForm);
  const dependents = useSelector(getDependents);
  let enrollment = [];

  const disableDependents = useSelector((state) => selector(state, "employee.isEnrolling") === "No");

  const employeeIsEnrolling = useSelector((state) => selector(state, "employee.isEnrolling") === "Yes");

  const enrollingDependents = dependents.filter((dep) => useSelector((state) => selector(state, `dependents[${dep.id}].isEnrolling`) === "Yes"));

  if (employee && employeeIsEnrolling) {
    enrollment = [employee.id, ...enrollingDependents.map((dep) => dep.id)];
  }

  return (
    <Table striped>
      <ChooseCoverageEnrollmentTableHead pricingType={pricingType} />
      <tbody>
        {chooseCoverageRows(familyInfo, prices, contributions, enrollment, plan.type).map((row) => (
          <ChooseCoverageRow
            row={row}
            index={index}
            pricingType={pricingType}
            familyInfo={familyInfo}
            enrollment={enrollment}
            key={row.label}
            disableDependents={disableDependents}
          />
        ))}

        {(pricingType === "segment" || pricingType === "composite_segment") && <SegmentDeclineCoverageRow enrollment={enrollment} />}

        {pricingType === "age" && <AgeTableTotal rows={chooseCoverageRows(familyInfo, prices, contributions, enrollment, plan.type)} index={index} />}
      </tbody>
    </Table>
  );
};

const ChooseCoverageEnrollmentTableHead = ({ pricingType }) =>
  pricingType === "age" ? (
    <thead>
      <tr>
        <th>Name</th>
        <th className={bem("Cell", "enroll")}>Enroll in Coverage</th>
        <th className={bem("Cell", "cost")}>Monthly Cost</th>
      </tr>
    </thead>
  ) : (
    <thead>
      <tr>
        <th className={bem("Cell", "segmentEnroll")}>Who is covered</th>
        <th className={bem("Cell", "cost")}>My Monthly Cost</th>
      </tr>
    </thead>
  );

const ChooseCoverageRow = ({ pricingType, ...props }) =>
  pricingType === "age" ? <AgeChooseCoverageRow {...props} /> : <SegmentChooseCoverageRow {...props} />;

const SegmentChooseCoverageRow = ({ index, row }) => (
  <tr>
    <td className={classnames(bem("Cell", "segmentEnroll"), bem("Cell", "radioButton"))}>
      <label>
        <Field component="input" type="radio" name="segment" value={row.rowId} validate={validateRequired} />

        {row.label}
      </label>
    </td>
    <td className={bem("Cell", "cost")}>{formatCurrency(row.prices[index])}</td>
  </tr>
);

const validateDependentsRequired = (value, allValues) => {
  if (allValues.employee?.isEnrolling != "No") {
    return validateRequired(value);
  }
};

const AgeChooseCoverageRow = ({ index, row, familyInfo, disableDependents }) => {
  const person = getPersonFromId(row.rowId, familyInfo);
  const disabled = disableDependents && person && person.type !== "employee";
  return (
    <tr className={classnames(bem("Row", row.type), bem("Row", row.applyPrice ? "apply" : "dontApply"))}>
      <td>{row.label}</td>
      <td className={classnames(bem("Cell", "radioButton"), bem("Cell", "enroll"))}>
        {person && (
          <div>
            <label>
              <Field
                component="input"
                type="radio"
                name={person.type === "employee" ? "employee.isEnrolling" : `dependents[${person.member_id}].isEnrolling`}
                value="Yes"
                validate={person.type === "employee" ? validateRequired : validateDependentsRequired}
                disabled={disabled}
              />
              Yes
            </label>
            <label>
              <Field
                component="input"
                type="radio"
                name={person.type === "employee" ? "employee.isEnrolling" : `dependents[${person.member_id}].isEnrolling`}
                value="No"
                validate={person.type === "employee" ? validateRequired : validateDependentsRequired}
                disabled={disabled}
              />
              No
            </label>
          </div>
        )}
      </td>
      <td className={bem("Cell", "cost")}>{formatCurrency(row.prices[index])}</td>
    </tr>
  );
};

const AgeTableTotal = ({ rows, index }) => (
  <tr className={bem("Row", "totalCost")}>
    <td>Total Monthly Cost</td>
    <td />
    <td className={bem("Cell", "cost")}>{formatCurrency(totalCost(rows, index))}</td>
  </tr>
);

const SegmentDeclineCoverageRow = () => (
  <tr>
    <td className={classnames(bem("Cell", "segmentEnroll"), bem("Cell", "radioButton"))}>
      <label>
        <Field component="input" type="radio" name="segment" value="none" validate={validateRequired} />
        Decline All Coverage
      </label>
    </td>
    <td className={bem("Cell", "cost")}>$0</td>
  </tr>
);

export default ChooseCoverageModal;
